import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import { format, addDays, parseISO } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';

const PlansSection = ({ trainerId, onRefresh, onPlanSelect, onPreviewSelect }) => {
  const [plans, setPlans] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [sourcePlan, setSourcePlan] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [masterPlanId, setMasterPlanId] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [updatingId, setUpdatingId] = useState(null);

  const getTodayStr = () => format(new Date(), 'yyyy-MM-dd');

  const initialFormState = {
    client_id: '',
    plan_name: '',
    start_date: getTodayStr(),
    price: 0,
    is_paid: false,
    goals: '',
    resource_url: ''
  };

  const [formData, setFormData] = useState(initialFormState);

  useEffect(() => {
    if (trainerId) fetchInitialData();
  }, [trainerId]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const { data: profile } = await supabase
        .from('trainer_profiles')
        .select('master_plan_id')
        .eq('id', trainerId)
        .single();

      if (profile) setMasterPlanId(profile.master_plan_id);

      const { data: clientData } = await supabase
        .from('training_clients')
        .select('id, owner_name, dog_name')
        .eq('trainer_id', trainerId)
        .order('dog_name');

      setClients(clientData || []);

      const clientIds = clientData?.map(c => c.id) || [];
      if (clientIds.length > 0) {
        const { data: planData } = await supabase
          .from('training_plans')
          .select(`*, training_clients ( id, owner_name, dog_name )`)
          .in('client_id', clientIds)
          .order('created_at', { ascending: false });

        setPlans(planData || []);
      }
    } catch (err) {
      console.error('Error loading plans:', err);
    } finally {
      setLoading(false);
    }
  };

  const calculateEndDate = async (startDate, planId) => {
    if (!planId) return startDate;
    const { data: tasks } = await supabase
      .from('training_tasks')
      .select('week_number, day_number')
      .eq('plan_id', planId);
    
    if (!tasks || tasks.length === 0) return startDate;

    const maxDays = tasks.reduce((max, t) => {
      const days = ((t.week_number - 1) * 7) + (t.day_number - 1);
      return Math.max(max, days);
    }, 0);
    
    return format(addDays(parseISO(startDate), maxDays), 'yyyy-MM-dd');
  };

  const handleDuplicatePlan = async (e) => {
    e.preventDefault();
    if (!sourcePlan || !formData.client_id || isSubmitting) return;
    setIsSubmitting(true);

    try {
      const { data: tasks } = await supabase
        .from('training_tasks')
        .select('*')
        .eq('plan_id', sourcePlan.id);

      const activeStartDate = formData.start_date || getTodayStr();
      let endDate = activeStartDate;
      
      if (tasks && tasks.length > 0) {
        const maxDays = tasks.reduce((max, t) => {
          const days = ((t.week_number - 1) * 7) + (t.day_number - 1);
          return Math.max(max, days);
        }, 0);
        endDate = format(addDays(parseISO(activeStartDate), maxDays), 'yyyy-MM-dd');
      }

      const { data: newPlan, error: planError } = await supabase
        .from('training_plans')
        .insert([{
          client_id: formData.client_id,
          plan_name: formData.plan_name || `${sourcePlan.plan_name} (Copy)`,
          start_date: activeStartDate,
          end_date: endDate,
          price: sourcePlan.price || 0,
          payment_link: sourcePlan.payment_link,
          is_paid: formData.is_paid,
          goals: sourcePlan.goals,
          resource_url: sourcePlan.resource_url
        }])
        .select().single();

      if (planError) throw planError;

      if (tasks && tasks.length > 0) {
        const clonedTasks = tasks.map(task => {
          const { id, created_at, updated_at, is_completed, session_score, video_submission_url, video_reviewed, completed_checkboxes, ...taskData } = task;
          const daysToAdd = ((task.week_number - 1) * 7) + (task.day_number - 1);
          const taskDate = format(addDays(parseISO(activeStartDate), daysToAdd), 'yyyy-MM-dd');

          return {
            ...taskData,
            plan_id: newPlan.id,
            task_date: taskDate,
            is_completed: false,
            session_score: null,
            video_submission_url: null,
            video_reviewed: false,
            completed_checkboxes: []
          };
        });
        await supabase.from('training_tasks').insert(clonedTasks);
      }

      setSuccessMessage('Plan duplicated with real-time dates!');
      setShowDuplicateModal(false);
      fetchInitialData();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      alert('Failed: ' + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const activeStartDate = formData.start_date || getTodayStr();
      
      // If we are creating a new plan and have a master plan, calculate the end date
      let endDate = activeStartDate;
      if (!editingPlan && masterPlanId) {
        endDate = await calculateEndDate(activeStartDate, masterPlanId);
      } else if (editingPlan) {
        // If editing, use existing end_date or recalculate if start changed
        endDate = await calculateEndDate(activeStartDate, editingPlan.id);
      }

      const payload = {
        client_id: formData.client_id,
        plan_name: formData.plan_name,
        start_date: activeStartDate,
        end_date: endDate,
        price: Number(formData.price),
        is_paid: formData.is_paid,
        goals: formData.goals?.trim() || null,
        resource_url: formData.resource_url?.trim() || null
      };

      if (editingPlan) {
        await supabase.from('training_plans').update(payload).eq('id', editingPlan.id);
      } else {
        await supabase.from('training_plans').insert([payload]);
      }

      setShowCreateForm(false);
      fetchInitialData();
      onRefresh?.();
      setSuccessMessage('Plan saved with auto-calculated duration!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      alert('Error saving plan: ' + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const setAsMaster = async (planId) => {
    setUpdatingId(planId);
    try {
      await supabase.from('trainer_profiles').update({ master_plan_id: planId }).eq('id', trainerId);
      setMasterPlanId(planId);
      setSuccessMessage('Master Template updated!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } finally { setUpdatingId(null); }
  };

  const toggleAccess = async (planId, currentStatus) => {
    setUpdatingId(planId);
    try {
      await supabase.from('training_plans').update({ is_paid: !currentStatus }).eq('id', planId);
      fetchInitialData();
    } finally { setUpdatingId(null); }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center bg-white p-6 rounded-[2.5rem] border border-gray-100 shadow-sm">
        <div>
          <h2 className="text-2xl font-black text-gray-900">Training Plans</h2>
          <p className="text-sm text-gray-500 font-medium">Manage access and curricula</p>
        </div>
        <button 
          onClick={() => { setEditingPlan(null); setFormData(initialFormState); setShowCreateForm(true); }}
          className="bg-indigo-600 text-white px-6 py-3 rounded-2xl font-black shadow-xl hover:bg-indigo-700 transition-all flex items-center gap-2"
        >
          <SafeIcon name="Plus" /> New Plan
        </button>
      </div>

      {successMessage && (
        <div className="p-4 bg-indigo-600 text-white rounded-2xl font-bold animate-in fade-in slide-in-from-top-2 flex items-center gap-2 shadow-lg">
          <SafeIcon name="CheckCircle" /> {successMessage}
        </div>
      )}

      <div className="grid gap-4">
        {plans.map(plan => (
          <div key={plan.id} className={`bg-white rounded-[2.5rem] border p-6 flex flex-col md:flex-row justify-between items-center gap-6 group transition-all ${plan.id === masterPlanId ? 'border-indigo-500 ring-4 ring-indigo-50' : 'border-gray-100 shadow-sm'}`}>
            <div className="flex-1">
              <div className="flex items-center flex-wrap gap-2 mb-2">
                <span className="text-[10px] font-black text-indigo-500 uppercase bg-indigo-50 px-2 py-0.5 rounded-md">
                  {plan.training_clients?.dog_name}
                </span>
                {plan.id === masterPlanId && (
                  <span className="bg-indigo-600 text-white text-[10px] font-black px-2 py-0.5 rounded-md flex items-center gap-1">
                    <SafeIcon name="Star" className="w-3 h-3 fill-current" /> Master Template
                  </span>
                )}
                <div className={`px-2 py-0.5 rounded-md text-[10px] font-black uppercase tracking-widest ${plan.is_paid ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                  {plan.is_paid ? 'Access Active' : 'Payment Pending'}
                </div>
              </div>
              <h3 className="text-xl font-black text-gray-900">{plan.plan_name}</h3>
              <p className="text-xs text-gray-400 font-bold mt-1 uppercase tracking-wider">
                {plan.start_date ? format(parseISO(plan.start_date), 'MMM d, yyyy') : 'TBD'} - {plan.end_date ? format(parseISO(plan.end_date), 'MMM d, yyyy') : 'TBD'}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button 
                onClick={() => {
                  setSourcePlan(plan);
                  setFormData({
                    ...initialFormState,
                    plan_name: `${plan.plan_name} (Copy)`,
                    price: plan.price,
                    goals: plan.goals || '',
                    resource_url: plan.resource_url || '',
                    start_date: getTodayStr()
                  });
                  setShowDuplicateModal(true);
                }}
                className="p-3 text-gray-400 hover:text-indigo-600 hover:bg-gray-50 rounded-xl transition-all"
                title="Duplicate Plan"
              >
                <SafeIcon name="Copy" className="w-5 h-5" />
              </button>

              <button 
                onClick={() => toggleAccess(plan.id, plan.is_paid)}
                disabled={updatingId === plan.id}
                className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${plan.is_paid ? 'bg-orange-50 text-orange-600' : 'bg-green-50 text-green-600'}`}
              >
                {plan.is_paid ? 'Revoke Access' : 'Grant Access'}
              </button>
              
              <button 
                onClick={() => onPlanSelect?.(plan)}
                className="px-6 py-3 bg-indigo-600 text-white rounded-2xl text-sm font-black shadow-lg hover:bg-indigo-700"
              >
                Manage Tasks
              </button>

              <div className="flex gap-1">
                <button onClick={() => onPreviewSelect?.(plan)} className="p-3 text-gray-400 hover:text-indigo-600 hover:bg-gray-50 rounded-xl transition-all" title="Preview Portal"><SafeIcon name="Search" /></button>
                <button onClick={() => { setEditingPlan(plan); setFormData({ ...initialFormState, ...plan }); setShowCreateForm(true); }} className="p-3 text-gray-400 hover:text-indigo-600 hover:bg-gray-50 rounded-xl transition-all" title="Edit Plan"><SafeIcon name="Edit" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Create/Edit Plan Modal */}
      <AnimatePresence>
        {showCreateForm && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center p-4 z-[9999]" onClick={() => setShowCreateForm(false)}>
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-[3rem] p-8 w-full max-w-2xl shadow-2xl overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <h3 className="text-2xl font-black text-gray-900 mb-6">{editingPlan ? 'Edit Plan' : 'New Plan'}</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Client</label>
                    <select required value={formData.client_id} onChange={e => setFormData({ ...formData, client_id: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-50 border-2 border-transparent rounded-xl outline-none font-bold focus:border-indigo-500"
                    >
                      <option value="">Select Client...</option>
                      {clients.map(c => <option key={c.id} value={c.id}>{c.dog_name} ({c.owner_name})</option>)}
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Plan Name</label>
                    <input required value={formData.plan_name} onChange={e => setFormData({ ...formData, plan_name: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-50 border-2 border-transparent rounded-xl outline-none font-bold focus:border-indigo-500" 
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Start Date</label>
                    <input type="date" value={formData.start_date} onChange={e => setFormData({ ...formData, start_date: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-50 border-2 border-transparent rounded-xl outline-none font-bold focus:border-indigo-500" 
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Price ($)</label>
                    <input type="number" value={formData.price} onChange={e => setFormData({ ...formData, price: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-50 border-2 border-transparent rounded-xl outline-none font-bold focus:border-indigo-500" 
                    />
                  </div>
                </div>

                <div className="flex gap-4 mt-8">
                  <button type="submit" disabled={isSubmitting} className="flex-1 bg-indigo-600 text-white py-4 rounded-2xl font-black shadow-xl hover:bg-indigo-700">
                    {isSubmitting ? 'Saving...' : 'Save Plan'}
                  </button>
                  <button type="button" onClick={() => setShowCreateForm(false)} className="flex-1 bg-gray-50 text-gray-500 py-4 rounded-2xl font-black hover:bg-gray-100">Cancel</button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Duplicate Plan Modal */}
      <AnimatePresence>
        {showDuplicateModal && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center p-4 z-[9999]" onClick={() => setShowDuplicateModal(false)}>
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-[3rem] p-8 w-full max-w-lg shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center">
                  <SafeIcon name="Copy" className="w-5 h-5" />
                </div>
                <h3 className="text-2xl font-black text-gray-900">Duplicate Plan</h3>
              </div>
              <p className="text-gray-500 text-sm font-medium mb-8">Cloning <b>{sourcePlan?.plan_name}</b>. The new schedule starts today.</p>
              
              <form onSubmit={handleDuplicatePlan} className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Target Client</label>
                  <select required value={formData.client_id} onChange={e => setFormData({ ...formData, client_id: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-50 border-2 border-transparent rounded-xl outline-none font-bold focus:border-indigo-500"
                  >
                    <option value="">Select Client for Copy...</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.dog_name} ({c.owner_name})</option>)}
                  </select>
                </div>

                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Start Date</label>
                  <input type="date" value={formData.start_date} onChange={e => setFormData({ ...formData, start_date: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-50 border-2 border-transparent rounded-xl outline-none font-bold focus:border-indigo-500" 
                  />
                </div>

                <div className="flex gap-4 mt-8">
                  <button type="submit" disabled={isSubmitting || !formData.client_id} className="flex-[2] bg-indigo-600 text-white py-4 rounded-2xl font-black shadow-xl hover:bg-indigo-700 disabled:opacity-50">
                    {isSubmitting ? 'Duplicating...' : 'Duplicate & Create'}
                  </button>
                  <button type="button" onClick={() => setShowDuplicateModal(false)} className="flex-1 bg-gray-50 text-gray-500 py-4 rounded-2xl font-black hover:bg-gray-100">Cancel</button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PlansSection;